import {
	AccountDeleteResponse,
	type AccountUpdateBody,
	AccountUpdateResponse,
	type ActivityCreateBody,
	ActivityCreateResponse,
	ActivityDeleteResponse,
	type ActivityId,
	ActivityListResponse,
	type ActivityUpdateBody,
	ActivityUpdateResponse,
	ActivityVisitResponse,
	type AppleSignInBody,
	AuthMeResponse,
	GeocodeResponse,
	type GroceryBatchCreateBody,
	GroceryBatchCreateResponse,
	type GroceryClearTickedBody,
	GroceryClearTickedResponse,
	type GroceryCreateBody,
	GroceryCreateResponse,
	type GroceryId,
	GroceryListResponse,
	type GroceryUpdateBody,
	GroceryUpdateResponse,
	AuthRequestBody,
	AuthRequestResponse,
	AuthVerifyBody,
	AuthVerifyResponse,
	type EmailChangeRequestBody,
	EmailChangeRequestResponse,
	type EmailChangeVerifyBody,
	EmailChangeVerifyResponse,
	ErrorResponse,
	ExportJobResponse,
	ExportLinkResponse,
	HouseholdMembersResponse,
	type HouseholdUpdateBody,
	HouseholdUpdateResponse,
	type ImageUploadBody,
	type MealDate,
	MealPlanListResponse,
	type MealPlanPutBody,
	MealPlanPutResponse,
	OnboardingBody,
	OnboardingResponse,
	type RecipeCreateBody,
	RecipeCreateResponse,
	RecipeDeleteResponse,
	type RecipeId,
	RecipeListResponse,
	type RecipeUpdateBody,
	RecipeUpdateResponse,
	ReverseGeocodeResponse,
	TodoClearCompletedBody,
	TodoClearCompletedResponse,
	TodoCreateBody,
	TodoCreateResponse,
	TodoDeleteResponse,
	type TodoId,
	TodoListResponse,
	type Mode,
	TodoUpdateBody,
	TodoUpdateResponse,
} from '@hewey/shared'
import type { z } from 'zod'
import { API_BASE } from '@config'
import { clearToken, getToken, setToken, TOKEN_AUTH } from '@auth/token.ts'

export class ApiError extends Error {
	constructor(
		public status: number,
		public code: string,
		message: string,
	) {
		super(message)
	}
}

// Every call to the API goes through here. The website sends the HttpOnly
// cookie. The iOS build sends its stored token as a Bearer header and forgets
// the token once the server rejects it.
export const authedFetch = async (
	path: string,
	init: RequestInit = {},
): Promise<Response> => {
	if (!TOKEN_AUTH) {
		return fetch(`${API_BASE}${path}`, { ...init, credentials: 'include' })
	}
	const headers = new Headers(init.headers)
	const token = await getToken()
	if (token) headers.set('authorization', `Bearer ${token}`)
	const res = await fetch(`${API_BASE}${path}`, { ...init, headers })
	if (res.status === 401) await clearToken()
	return res
}

const request = async <T>(
	method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
	path: string,
	schema: z.ZodType<T>,
	body?: unknown,
): Promise<T> => {
	const init: RequestInit = { method }
	if (body !== undefined) {
		init.headers = { 'content-type': 'application/json' }
		init.body = JSON.stringify(body)
	}
	const res = await authedFetch(path, init)

	const raw = res.status === 204 ? null : await res.json().catch(() => null)

	if (!res.ok) {
		const parsed = ErrorResponse.safeParse(raw)
		if (parsed.success) {
			throw new ApiError(
				res.status,
				parsed.data.error.code,
				parsed.data.error.message,
			)
		}
		throw new ApiError(res.status, 'unknown_error', res.statusText)
	}

	return schema.parse(raw)
}

export const api = {
	authRequest: (body: AuthRequestBody) =>
		request('POST', '/auth/request', AuthRequestResponse, body),
	authVerify: async (body: AuthVerifyBody) => {
		const res = await request(
			'POST',
			'/auth/verify',
			AuthVerifyResponse,
			TOKEN_AUTH ? { ...body, returnToken: true } : body,
		)
		if (res.token) await setToken(res.token)
		return res
	},
	authApple: async (body: AppleSignInBody) => {
		const res = await request(
			'POST',
			'/auth/apple',
			AuthVerifyResponse,
			TOKEN_AUTH ? { ...body, returnToken: true } : body,
		)
		if (res.token) await setToken(res.token)
		return res
	},
	authMe: async () => {
		const res = await request('GET', '/auth/me', AuthMeResponse)
		if (!res.user) await clearToken()
		return res
	},
	authLogout: async () => {
		try {
			return await request(
				'POST',
				'/auth/logout',
				AuthRequestResponse /* {ok: true} */,
			)
		} finally {
			await clearToken()
		}
	},
	authOnboarding: (body: OnboardingBody) =>
		request('POST', '/auth/onboarding', OnboardingResponse, body),

	account: {
		update: (body: AccountUpdateBody) =>
			request('PATCH', '/account', AccountUpdateResponse, body),
		email: {
			request: (body: EmailChangeRequestBody) =>
				request(
					'POST',
					'/account/email/request',
					EmailChangeRequestResponse,
					body,
				),
			verify: (body: EmailChangeVerifyBody) =>
				request(
					'POST',
					'/account/email/verify',
					EmailChangeVerifyResponse,
					body,
				),
		},
		export: {
			start: () => request('POST', '/account/export', ExportJobResponse),
			status: () => request('GET', '/account/export', ExportJobResponse),
			// Website only. Deliberately a URL and not a fetch. Followed as a
			// plain link it's a top-level GET, so the session cookie rides
			// along under SameSite=Lax, and the 302's Content-Disposition makes
			// the browser save the file instead of navigating away from
			// Settings.
			downloadUrl: `${API_BASE}/account/export/download`,
			// iOS build only. A link can't carry the Bearer token, so this
			// fetches the presigned S3 URL the 302 would point at.
			link: () =>
				request(
					'GET',
					'/account/export/download?format=json',
					ExportLinkResponse,
				),
		},
		remove: async () => {
			const res = await request(
				'DELETE',
				'/account',
				AccountDeleteResponse,
			)
			await clearToken()
			return res
		},
	},

	household: {
		members: () =>
			request('GET', '/household/members', HouseholdMembersResponse),
		rename: (body: HouseholdUpdateBody) =>
			request('PATCH', '/household', HouseholdUpdateResponse, body),
	},

	mealPlan: {
		list: (from: MealDate, to: MealDate) =>
			request(
				'GET',
				`/meal-plan?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
				MealPlanListResponse,
			),
		put: (date: MealDate, body: MealPlanPutBody) =>
			request(
				'PUT',
				`/meal-plan/${encodeURIComponent(date)}`,
				MealPlanPutResponse,
				body,
			),
	},

	recipe: {
		list: () => request('GET', '/recipes', RecipeListResponse),
		create: (body: RecipeCreateBody) =>
			request('POST', '/recipes', RecipeCreateResponse, body),
		update: (id: RecipeId, body: RecipeUpdateBody) =>
			request('PATCH', `/recipes/${id}`, RecipeUpdateResponse, body),
		delete: (id: RecipeId) =>
			request('DELETE', `/recipes/${id}`, RecipeDeleteResponse),
		// Custom photo. Viewing goes through the GET proxy (see useImageSrc),
		// not a JSON call — only mutations go through `request`.
		uploadImage: (id: RecipeId, body: ImageUploadBody) =>
			request('POST', `/recipes/${id}/image`, RecipeUpdateResponse, body),
		removeImage: (id: RecipeId) =>
			request('DELETE', `/recipes/${id}/image`, RecipeUpdateResponse),
	},

	activity: {
		list: () => request('GET', '/activities', ActivityListResponse),
		create: (body: ActivityCreateBody) =>
			request('POST', '/activities', ActivityCreateResponse, body),
		update: (id: ActivityId, body: ActivityUpdateBody) =>
			request('PATCH', `/activities/${id}`, ActivityUpdateResponse, body),
		delete: (id: ActivityId) =>
			request('DELETE', `/activities/${id}`, ActivityDeleteResponse),
		visit: (id: ActivityId) =>
			request('POST', `/activities/${id}/visits`, ActivityVisitResponse),
		uploadImage: (id: ActivityId, body: ImageUploadBody) =>
			request(
				'POST',
				`/activities/${id}/image`,
				ActivityUpdateResponse,
				body,
			),
		removeImage: (id: ActivityId) =>
			request(
				'DELETE',
				`/activities/${id}/image`,
				ActivityUpdateResponse,
			),
		geocode: (q: string) =>
			request(
				'GET',
				`/activities/geocode?q=${encodeURIComponent(q)}`,
				GeocodeResponse,
			),
		reverseGeocode: (lat: number, lng: number) =>
			request(
				'GET',
				`/activities/reverse-geocode?lat=${lat}&lng=${lng}`,
				ReverseGeocodeResponse,
			),
	},

	todo: {
		list: (mode: Mode) =>
			request(
				'GET',
				`/todos?mode=${encodeURIComponent(mode)}`,
				TodoListResponse,
			),
		create: (body: TodoCreateBody) =>
			request('POST', '/todos', TodoCreateResponse, body),
		update: (id: TodoId, body: TodoUpdateBody) =>
			request('PATCH', `/todos/${id}`, TodoUpdateResponse, body),
		delete: (id: TodoId) =>
			request('DELETE', `/todos/${id}`, TodoDeleteResponse),
		clearCompleted: (body: TodoClearCompletedBody) =>
			request(
				'POST',
				'/todos/clear-completed',
				TodoClearCompletedResponse,
				body,
			),
	},

	grocery: {
		list: (mode: Mode) =>
			request(
				'GET',
				`/groceries?mode=${encodeURIComponent(mode)}`,
				GroceryListResponse,
			),
		create: (body: GroceryCreateBody) =>
			request('POST', '/groceries', GroceryCreateResponse, body),
		batchCreate: (body: GroceryBatchCreateBody) =>
			request(
				'POST',
				'/groceries/batch',
				GroceryBatchCreateResponse,
				body,
			),
		update: (id: GroceryId, body: GroceryUpdateBody) =>
			request('PATCH', `/groceries/${id}`, GroceryUpdateResponse, body),
		clearTicked: (body: GroceryClearTickedBody) =>
			request(
				'POST',
				'/groceries/clear-ticked',
				GroceryClearTickedResponse,
				body,
			),
	},
}
